import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import PassagePicker from '../components/PassagePicker';
import DashboardHeader from '../components/DashboardHeader';
import { passageIdFromLocationSearch } from '../utils/passageUrl';
import { preprocessParagraphText } from '../utils/textProcess';
import './Dashboard.css';

/** One DB paragraph row → one line for bulk insert (internal newlines flattened). */
function paragraphsToBulkText(paragraphs) {
  if (!paragraphs || paragraphs.length === 0) return '';
  return paragraphs
    .map((row) => String(row.value ?? '').replace(/\r\n|\n|\r/g, ' ').trim())
    .join('\n');
}

async function fetchParagraphEditor(passageIdStr) {
  const res = await api.get('/data/paragraphs', {
    params: { passageId: passageIdStr },
  });
  const paragraphs = res.data.paragraphs || [];
  return {
    text: paragraphsToBulkText(paragraphs),
    hadExisting: paragraphs.length > 0,
  };
}

const Paragraph = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  const [passageId, setPassageId] = useState(passageIdFromLocationSearch);
  const [text, setText] = useState('');
  const [preprocessInput, setPreprocessInput] = useState('');
  const [paragraphLoadLoading, setParagraphLoadLoading] = useState(false);
  const [paragraphLoadError, setParagraphLoadError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasExistingParagraphs, setHasExistingParagraphs] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPassagesLoading(true);
      setPassagesError('');
      try {
        const res = await api.get('/data/passages', { params: { all: '1' } });
        if (!cancelled) {
          setPassages(res.data.passages || []);
          setPassagesError('');
        }
      } catch (err) {
        if (!cancelled) {
          setPassagesError(
            err.response?.data?.error ||
              err.response?.data?.details ||
              'Failed to load passages'
          );
          setPassages([]);
        }
      } finally {
        if (!cancelled) setPassagesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!passageId) {
      setText('');
      setPreprocessInput('');
      setParagraphLoadError('');
      setParagraphLoadLoading(false);
      setHasExistingParagraphs(false);
      return;
    }
    let cancelled = false;
    setParagraphLoadLoading(true);
    setParagraphLoadError('');
    setText('');
    setPreprocessInput('');
    (async () => {
      try {
        const { text: bulk, hadExisting } = await fetchParagraphEditor(passageId);
        if (cancelled) return;
        setText(bulk);
        setHasExistingParagraphs(hadExisting);
      } catch (err) {
        if (cancelled) return;
        setParagraphLoadError(
          err.response?.data?.error ||
            err.response?.data?.details ||
            'Failed to load paragraphs'
        );
      } finally {
        if (!cancelled) setParagraphLoadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [passageId]);

  const performInsertParagraphs = async () => {
    setMessage({ type: '', text: '' });
    setSubmitLoading(true);
    try {
      const res = await api.post('/data/paragraphs/bulk', {
        passageId: parseInt(passageId, 10),
        text,
      });
      if (res.data?.mode === 'update') {
        const { changed = 0, carried = {}, wordsReset = 0, needsManual = [] } = res.data;
        let summary = `${res.data.message || 'Paragraphs updated'} (${changed} changed`;
        if (changed > 0) {
          summary += `; carried to ${carried.sentences || 0} sentence(s), ${carried.shortSentences || 0} short sentence(s), ${carried.words || 0} word(s)`;
        }
        summary += ')';
        if (wordsReset > 0) {
          summary += ` — words on ${wordsReset} short sentence(s) were cleared, redo them on the Word page`;
        }
        if (needsManual.length > 0) {
          summary += `. Still to fix by hand: ${needsManual.join('; ')}`;
        }
        setMessage({ type: 'success', text: summary });
      } else {
        const n = res.data?.inserted;
        const ids = (res.data?.rows || []).map((r) => r.paragraph_id).join(', ');
        setMessage({
          type: 'success',
          text:
            n != null
              ? `${res.data?.message || 'Saved'} (${n} paragraph(s)${ids ? `; ids: ${ids}` : ''})`
              : res.data?.message || 'Paragraphs created',
        });
      }
      try {
        const { text: bulk, hadExisting } = await fetchParagraphEditor(passageId);
        setText(bulk);
        setHasExistingParagraphs(hadExisting);
        setParagraphLoadError('');
      } catch (refetchErr) {
        setParagraphLoadError(
          refetchErr.response?.data?.error ||
            refetchErr.response?.data?.details ||
            'Saved, but could not reload paragraphs from the server.'
        );
      }
      return true;
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        'Failed to save paragraphs';
      setMessage({ type: 'error', text: msg });
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await performInsertParagraphs();
  };

  const handleInsertAndGoSentence = async () => {
    const ok = await performInsertParagraphs();
    if (ok && passageId) navigate(`/sentence?passageId=${encodeURIComponent(passageId)}`);
  };

  const handlePreprocessConfirm = () => {
    const output = preprocessParagraphText(preprocessInput);
    if (!output) return;
    setText((prev) => (prev.trim() ? `${prev.trimEnd()}\n${output}` : output));
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="New paragraphs" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 720, margin: '0 auto' }}>
        {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

        <div className="form-section">
          <form onSubmit={handleSubmit} className="insert-form">
            <h2>Insert into paragraph</h2>

            <div className="form-group">
              <PassagePicker
                inputId="paragraph-passage-picker"
                label={
                  <>
                    Passage <span className="required">*</span>
                  </>
                }
                passages={passages}
                passagesLoading={passagesLoading}
                passagesError={passagesError}
                value={passageId}
                onChange={setPassageId}
                hiddenRequired
                extraDisabled={paragraphLoadLoading}
              />
            </div>

            <div className="form-group">
              <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>Pre-process</h3>
              <label htmlFor="paragraph-preprocess-input">Source text</label>
              <textarea
                id="paragraph-preprocess-input"
                value={preprocessInput}
                onChange={(e) => setPreprocessInput(e.target.value)}
                rows={8}
                disabled={paragraphLoadLoading}
                placeholder="Paste raw source text here."
              />
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="submit-button"
                  style={{ marginTop: 0 }}
                  disabled={paragraphLoadLoading || !preprocessInput.trim()}
                  onClick={handlePreprocessConfirm}
                >
                  Confirm
                </button>
              </div>
              <small className="form-hint">
                Removes <code>[1]</code>/<code>[2]</code> markers, all spaces, and{' '}
                <code>&quot;</code>/<code>&apos;</code>/<code>‘</code>/<code>’</code>/
                <code>“</code>/<code>”</code>/<code>「</code>/<code>」</code>/
                <code>『</code>/<code>』</code> quotes; converts{' '}
                <code>;</code> to <code>；</code>. Confirm appends result to Paragraph text below.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="paragraph-text">
                Paragraph text <span className="required">*</span>
              </label>
              {paragraphLoadLoading ? (
                <div className="loading-state" style={{ marginBottom: 8 }}>
                  Loading paragraphs…
                </div>
              ) : null}
              {paragraphLoadError ? (
                <div className="message error" style={{ marginBottom: 8 }}>
                  {paragraphLoadError}
                </div>
              ) : null}
              <textarea
                id="paragraph-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                required
                rows={14}
                disabled={paragraphLoadLoading}
                placeholder="One paragraph per line (non-empty lines become separate rows). Blank lines are ignored."
              />
              <small className="form-hint">
                Each non-empty line is one paragraph: <code>value</code> is that line;{' '}
                <code>order</code> increases within the selected passage; <code>paragraph_id</code> and{' '}
                <code>question_id</code> are assigned automatically. Selecting a passage loads existing
                paragraphs here when present.
              </small>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
              <button
                type="submit"
                className="submit-button"
                style={{ marginTop: 0 }}
                disabled={
                  submitLoading ||
                  paragraphLoadLoading ||
                  passagesLoading ||
                  !!passagesError ||
                  (!passagesLoading && passages.length === 0)
                }
              >
                {submitLoading
                  ? hasExistingParagraphs
                    ? 'Saving…'
                    : 'Inserting...'
                  : hasExistingParagraphs
                    ? 'Save paragraphs'
                    : 'Insert paragraphs'}
              </button>
              <button
                type="button"
                className="submit-button"
                style={{ marginTop: 0 }}
                disabled={
                  submitLoading ||
                  paragraphLoadLoading ||
                  passagesLoading ||
                  !!passagesError ||
                  (!passagesLoading && passages.length === 0)
                }
                onClick={handleInsertAndGoSentence}
              >
                {submitLoading
                  ? hasExistingParagraphs
                    ? 'Saving…'
                    : 'Inserting...'
                  : hasExistingParagraphs
                    ? 'Save paragraphs and go to Sentence'
                    : 'Insert paragraphs and go to Sentence'}
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Paragraph;
